import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  KUNDENARTEN,
  KUNDENMARKEN,
  KUNDENMARKENWORT,
  type Kundenart,
  type Kundenmarke as Marke,
  type Kundenzeile,
  liste,
} from '../api/kunden';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import type { Filtermarke } from '../komponenten/Filtermarken';
import { Liste } from '../komponenten/Liste';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { Kundenmarke } from './Kundenmarke';
import { Kundenvorschau } from './Kundenvorschau';

/** KUN-22: die Trefferliste beginnt bei drei Zeichen. */
const AB_ZEICHEN = 3;

type Filter = {
  readonly suche: string;
  /** Leer heisst: alle Arten. */
  readonly kundenarten: readonly Kundenart[];
  /** PRD-38 (CRM-171): eine Kennzeichnung oder alle. */
  readonly marke: Marke | null;
  readonly nurEigene: boolean;
};

const ARTNAME: Record<Kundenart, string> = {
  privat: 'Privat',
  geschaeft: 'Geschäft',
  oeffentlich: 'Öffentlich',
};

/**
 * M-10 Kundenliste — der haeufigste Einstieg ins System.
 *
 * DIE REFERENZ FÜR DEN SEITENTYP LISTE (ÄA-41): Der Seitenkopf trägt den
 * Zähler und die Haupthandlung, darunter die Filterleiste auf dem Grund —
 * Suche, Segmente, Filtermarken —, darunter die Rastertabelle in einer Karte
 * und rechts die Vorschau ohne Seitenwechsel.
 *
 * Drei Regeln aus dem Pflichtenheft stecken darin, und jede hat einen Grund,
 * der im Betrieb sichtbar wird:
 *
 *   * Aktive Filter stehen als Marken da und sind einzeln abwaehlbar. Ein
 *     Filter, den man nicht sieht, ist die haeufigste Ursache fuer "der Kunde
 *     ist nicht im System".
 *   * Gesperrte Kunden bleiben sichtbar und tragen ihr Kennzeichen (KUN-17).
 *     Sie auszublenden fuehrte nur dazu, dass sie neu angelegt werden.
 *   * Ohne Treffer wird die Neuanlage mit dem uebernommenen Suchbegriff
 *     vorgeschlagen, statt eine leere Tabelle zu zeigen.
 */

export function Kundenliste({
  beiOeffnen,
  beiNeu,
  anfangsSuche = '',
  vorbelegung = [],
}: {
  readonly beiOeffnen: (id: string) => void;
  readonly beiNeu: (name: string) => void;
  readonly anfangsSuche?: string;
  /**
   * Die Kundenarten, auf die die Liste beim Oeffnen steht (Tab. 11 i.d.F.
   * AeA-01, Auftrag #9): Vertrieb auf Geschaeft und Oeffentlich, Service auf
   * Privat.
   *
   * EINE VORBELEGUNG, KEINE SPERRE. Jede Art steht als eigene Marke da und
   * laesst sich abwaehlen — ein Filter, den man nicht sieht, ist die
   * haeufigste Ursache fuer „der Kunde ist nicht im System".
   *
   * Nur der ANFANGSWERT: Wechselt der Einstieg, stellt die Arbeitsflaeche eine
   * neue Liste hin (`key`), statt diese umzustellen.
   */
  readonly vorbelegung?: readonly Kundenart[];
}): JSX.Element {
  const [filter, setzeFilter] = useState<Filter>(() => ({
    suche: anfangsSuche,
    kundenarten: vorbelegung,
    marke: null,
    nurEigene: false,
  }));
  const [kunden, setzeKunden] = useState<readonly Kundenzeile[]>([]);
  const [mehr, setzeMehr] = useState(false);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [vorschau, setzeVorschau] = useState<Kundenzeile | undefined>(undefined);
  /* ÄA-41: Der Zähler steht im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(kunden.length, 'Kunde', 'Kunden', mehr));

  const laden = useCallback(async (f: Filter): Promise<void> => {
    setzeLaedt(true);
    try {
      const ergebnis = await liste({
        suche: f.suche.length >= AB_ZEICHEN ? f.suche : '',
        kundenarten: f.kundenarten,
        ...(f.marke === null ? {} : { marke: f.marke }),
        nurEigene: f.nurEigene,
      });
      setzeKunden(ergebnis.kunden);
      setzeMehr(ergebnis.mehrAlsGezeigt);
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  // Die Suche laeuft waehrend des Tippens, aber nicht bei jedem Anschlag: 250
  // Millisekunden Ruhe genuegen, damit ein Wort zu Ende getippt ist, und
  // halten die Zahl der Abfragen bei einem Bruchteil.
  useEffect(() => {
    const kennung = setTimeout(() => void laden(filter), 250);
    return () => clearTimeout(kennung);
  }, [filter, laden]);

  /* Je Art eine Marke: Wer im Vertrieb eine Behoerde sucht, nimmt nur die
     Geschaefte heraus und behaelt den Rest der Vorbelegung. Sichtbar steht nur
     der Wert; den Feldnamen hoert die Vorlesehilfe (Auftrag CRM-59). */
  const marken: readonly Filtermarke[] = [
    ...filter.kundenarten.map((art) => ({
      text: ARTNAME[art],
      feld: 'Kundenart',
      entfernen: () =>
        setzeFilter((f) => ({ ...f, kundenarten: f.kundenarten.filter((a) => a !== art) })),
    })),
    /* PRD-38 (CRM-171): die Kennzeichnung, etwa nur die Kostenträger. */
    ...(filter.marke === null
      ? []
      : [
          {
            text: KUNDENMARKENWORT[filter.marke],
            feld: 'Status',
            entfernen: () => setzeFilter((f) => ({ ...f, marke: null })),
          },
        ]),
    /* „Nur meine" steht im Segmentreiter daneben und braucht keine zweite Marke. */
  ];

  const zuKurz = filter.suche !== '' && filter.suche.length < AB_ZEICHEN;

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      <Liste
        suche={{
          wert: filter.suche,
          aendern: (wert) => setzeFilter((f) => ({ ...f, suche: wert })),
          beschriftung: 'Kunden suchen',
          platzhalter: 'Name, Nummer, Ort, E-Mail oder USt-IdNr.',
          beschrieben: 'liste-suchhilfe',
        }}
        marken={marken}
        filter={
          <>
            <Segmente
              beschriftung="Betreuung"
              gewaehlt={filter.nurEigene ? 'meine' : 'alle'}
              beiWahl={(w) => setzeFilter((f) => ({ ...f, nurEigene: w === 'meine' }))}
              optionen={[
                { wert: 'alle', text: 'Alle' },
                { wert: 'meine', text: 'Nur meine' },
              ]}
            />
            {/*
              Das Feld FUEGT HINZU und ersetzt nicht: Gewaehlte Arten stehen als
              Marken daneben, und „Alle Kundenarten" nimmt sie alle zurueck.
            */}
            <select
              className="liste__filter"
              value=""
              onChange={(e) => {
                const wert = e.target.value;
                setzeFilter((f) => ({
                  ...f,
                  kundenarten:
                    wert === ''
                      ? []
                      : KUNDENARTEN.filter((a) => a === wert || f.kundenarten.includes(a)),
                }));
              }}
              aria-label="Kundenart"
            >
              <option value="">
                {filter.kundenarten.length === 0 ? 'Alle Kundenarten' : 'Alle Kundenarten zeigen'}
              </option>
              {KUNDENARTEN.filter((a) => !filter.kundenarten.includes(a)).map((a) => (
                <option key={a} value={a}>
                  {`+ ${ARTNAME[a]}`}
                </option>
              ))}
            </select>
            {/* PRD-38 (CRM-171): eine Kennzeichnung ersetzt die vorige. */}
            <select
              className="liste__filter"
              value={filter.marke ?? ''}
              onChange={(e) => {
                const wert = e.target.value;
                setzeFilter((f) => ({
                  ...f,
                  marke: KUNDENMARKEN.find((m) => m === wert) ?? null,
                }));
              }}
              aria-label="Status"
            >
              <option value="">Alle Status</option>
              {KUNDENMARKEN.map((m) => (
                <option key={m} value={m}>
                  {KUNDENMARKENWORT[m]}
                </option>
              ))}
            </select>
          </>
        }
        hinweise={
          <>
            <p className="hilfe" id="liste-suchhilfe">
              Die Suche findet ab drei Zeichen und verzeiht bis zu zwei abweichende Zeichen je acht.
              „Reimers“ findet auch „Reimhold“ — das ist gewollt.
            </p>
            {zuKurz && (
              <p role="status">Noch {AB_ZEICHEN - filter.suche.length} Zeichen bis zur Suche.</p>
            )}
            {mehr && (
              <p className="warnung" role="status">
                Mehr als 500 Treffer. Schränken Sie die Suche ein — die Liste zeigt die ersten 500.
              </p>
            )}
          </>
        }
        vorschauNurMitZeiger
        vorschau={
          <>
            {vorschau === undefined ? (
              <p className="feld__hilfe">
                Fahren Sie über eine Zeile — die Vorschau erspart den Sprung in den Datensatz, wenn
                nur eine Auskunft gefragt ist.
              </p>
            ) : (
              /* M-10 i. d. F. AeA-45: Kontakt, Umsatz, Buchungen und Eckdaten, entprellt geladen. */
              <Kundenvorschau zeile={vorschau} beiOeffnen={beiOeffnen} />
            )}
          </>
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">
              {kunden.length} Kunden
              {filter.suche.length >= AB_ZEICHEN ? ', nach Treffergüte sortiert' : ''}
            </caption>
            <thead>
              <tr>
                {/* CRM-63: Nummer und Status knapp, Ort und Betreuer schmal, der Rest dem Namen. */}
                <th scope="col" className="liste__spalte--knapp">
                  Nummer
                </th>
                <th scope="col">Name</th>
                <th scope="col" className="liste__spalte--schmal">
                  Ort
                </th>
                <th scope="col" className="liste__spalte--knapp">
                  Status
                </th>
                <th scope="col" className="liste__spalte--schmal">
                  Betreuer
                </th>
              </tr>
            </thead>
            <tbody>
              {kunden.map((k) => (
                <tr
                  key={k.id}
                  className={vorschau?.id === k.id ? 'liste__zeile--gewaehlt' : undefined}
                  onMouseEnter={() => setzeVorschau(k)}
                  onFocus={() => setzeVorschau(k)}
                >
                  <td className="liste__spalte--knapp">
                    <button
                      type="button"
                      className="liste__oeffnen liste__nummer"
                      onClick={() => beiOeffnen(k.id)}
                    >
                      {k.kundennummer}
                    </button>
                  </td>
                  <td className="liste__name">{k.name}</td>
                  <td>{k.ort ?? '—'}</td>
                  <td>
                    <Kundenmarke marke={k.marke} />
                    {k.gesperrt && <span className="marke marke--gesperrt">gesperrt</span>}
                  </td>
                  <td>{k.betreuerName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!laedt && kunden.length === 0 && (
          <div className="liste__leer" role="status">
            {filter.suche.length >= AB_ZEICHEN ? (
              <>
                <p>Kein Treffer für „{filter.suche}“.</p>
                {/* M-10: Der Suchbegriff wandert in die Neuanlage. Wer sucht
                    und nichts findet, will fast immer genau das anlegen. */}
                <button
                  type="button"
                  className="knopf knopf--haupt"
                  onClick={() => beiNeu(filter.suche)}
                >
                  „{filter.suche}“ neu anlegen
                </button>
              </>
            ) : (
              <p>Suchen Sie nach Name, Nummer, Ort, E-Mail oder USt-IdNr.</p>
            )}
          </div>
        )}
      </Liste>
    </>
  );
}
